import { useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, Loader2, LockOpen } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useLinkToken } from "@/lib/hooks/use-link-token";
import { useNoIndexMeta } from "@/lib/hooks/use-noindex-meta";
import { showToast } from "@/lib/ui/toast";

interface UnlockSearch {
  token?: string;
}

/** What POST /api/auth/owner-proof-unlock answers once the codes are unlocked. */
interface UnlockAnswer {
  ok: true;
  signedOut?: boolean;
}

/**
 * The one-tap "Unlock and sign out everywhere" of the "too many wrong security
 * codes" alert (apps/api routes/auth-owner-proof-unlock.ts, 2026-10-10
 * review). The link goes to the email or phone the account has had the
 * longest; it needs no session. Opening this page does nothing — link
 * scanners and previews open it too — only the tap below unlocks the codes:
 * once, while that pause lasts. Every session of the account ends with it,
 * this browser's included. The token leaves the address bar on mount
 * (useLinkToken).
 */
export const Route = createFileRoute("/auth/unlock-security-codes")({
  validateSearch: (search: Record<string, unknown>): UnlockSearch => ({
    token: typeof search.token === "string" && search.token ? search.token : undefined,
  }),
  component: UnlockSecurityCodesPage,
});

type Done = "signed_out" | "sessions_kept";

function UnlockSecurityCodesPage() {
  const token = useLinkToken(Route.useSearch().token);
  const { t } = useLingui();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<Done | null>(null);
  const [error, setError] = useState<string | null>(null);

  useNoIndexMeta();

  /** What a refusal means to the person. */
  const messageOf = (err: unknown): string => {
    const body =
      err instanceof ApiError
        ? (err.body as { error?: unknown; retryAfter?: unknown } | null)
        : null;
    const code = body?.error;
    switch (code) {
      case "too_many_requests": {
        // These links are bounded per account and day (apps/api lib/signed-link.ts).
        const wait = typeof body?.retryAfter === "number" ? body.retryAfter : 86_400;
        const hours = Math.max(1, Math.ceil(wait / 3600));
        return t`Too many attempts with these links today. Try again in ${hours} h, or write to support@pontimed.app.`;
      }
      case "link_invalid":
        return t`This link is not valid. Open it straight from the most recent message we sent you.`;
      case "link_expired":
        return t`This link has expired. Write to support@pontimed.app and we'll help you.`;
      case "link_used":
        return t`This link was already used. If security codes are still paused, write to support@pontimed.app.`;
      case "not_locked":
        return t`Security codes on your account are no longer paused, so there is nothing to unlock.`;
      case "link_target_gone":
        return t`This link was sent to an address that is no longer on your account, so it no longer works. Write to support@pontimed.app and we'll help you.`;
    }
    return t`We couldn't unlock the codes. Try again in a moment.`;
  };

  const unlock = async () => {
    if (!token) return;
    setSubmitting(true);
    setError(null);
    const toast = showToast(t`Unlocking security codes…`, "loading");
    try {
      const res = await apiFetch.post<UnlockAnswer>(
        "/api/auth/owner-proof-unlock",
        { token },
        { skipAuthRefresh: true },
      );
      setDone(res?.signedOut === false ? "sessions_kept" : "signed_out");
      toast.update(t`Security codes unlocked`, "success");
    } catch (err) {
      // Unlocked, but the sessions could not be ended: say so.
      if (
        err instanceof ApiError &&
        (err.body as { error?: unknown } | null)?.error === "sessions_not_revoked"
      ) {
        setDone("sessions_kept");
        toast.update(t`Security codes unlocked`, "success");
        return;
      }
      const message = messageOf(err);
      setError(message);
      toast.update(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const signInAgain = () => {
    if (typeof window === "undefined") return;
    window.location.assign("/find?auth=open");
  };

  return (
    <main className="bg-background flex min-h-[100dvh] items-center justify-center px-4 py-10">
      <div className="bg-card border-border w-full max-w-md rounded-2xl border p-6 shadow-sm sm:p-8">
        {done ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <CheckCircle2 className="text-success size-10" aria-hidden />
            <h1 className="text-foreground text-lg font-bold">
              <Trans>Security codes are unlocked</Trans>
            </h1>
            {done === "signed_out" ? (
              <p className="text-muted-foreground text-sm">
                <Trans>
                  Every device signed in to your account has been signed out. Sign in again, then
                  check your sign-in methods, passkeys and two-factor authentication.
                </Trans>
              </p>
            ) : (
              <div className="border-warning-border bg-warning-soft text-warning-strong flex items-start gap-2 rounded-lg border p-3 text-left text-sm">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                <span>
                  <Trans>
                    We couldn't sign out the other devices on your account. Sign in, then use Sign
                    out everywhere in Settings → Security right away.
                  </Trans>
                </span>
              </div>
            )}
            <Button
              className="mt-2 h-12 w-full rounded-lg text-sm font-semibold"
              onClick={signInAgain}
            >
              <Trans>Sign in</Trans>
            </Button>
          </div>
        ) : !token ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <AlertTriangle className="text-muted-foreground size-10" aria-hidden />
            <h1 className="text-foreground text-lg font-bold">
              <Trans>Link not valid</Trans>
            </h1>
            <p className="text-muted-foreground text-sm">
              <Trans>Open the link straight from the message we sent you.</Trans>
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 text-center">
            <LockOpen className="text-primary size-10" aria-hidden />
            <h1 className="text-foreground text-lg font-bold">
              <Trans>Unlock security codes?</Trans>
            </h1>
            <p className="text-muted-foreground text-sm">
              <Trans>
                Security codes on your PontiMed account were paused after too many wrong codes.
                Unlock them here: every device signed in to your account is signed out too, so
                whoever entered them loses access.
              </Trans>
            </p>

            {error && (
              <p
                className="text-destructive flex items-start gap-1.5 text-left text-sm"
                role="alert"
              >
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                {error}
              </p>
            )}

            <Button
              className="mt-2 h-12 w-full gap-2 rounded-lg text-sm font-semibold"
              disabled={submitting}
              onClick={() => void unlock()}
            >
              {submitting ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <LockOpen className="size-4" aria-hidden />
              )}
              <Trans>Unlock and sign out everywhere</Trans>
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
